export type WebsiteStatus = "needs-replacing" | "terrible" | "starting-fresh";
export type LaunchTimeline = "asap" | "1-2-months" | "3-6-months" | "exploring";
export type PricingPreference = "launch" | "growth" | "custom" | "undecided";
export type DecisionMakers = "just-me" | "partner-spouse" | "team-manager" | "other";
export type PlanKey = "launch" | "growth" | "custom";

export type LeadFormData = {
  // Step 1
  industry?: string;
  industryOther?: string;

  // Step 2
  businessName?: string;
  websiteStatus?: WebsiteStatus;
  currentWebsiteUrl?: string;
  inspirationUrl?: string;

  // Step 3
  websiteGoal?: string[];

  // Step 4
  launchTimeline?: LaunchTimeline;

  // Step 5
  logoFile?: File;
  brandStyle?: string;

  // Step 6 (lead row INSERT happens here in Phase B)
  fullName?: string;
  email?: string;
  phoneCountry?: string;
  phoneNumber?: string;
  instagramHandle?: string;
  decisionMakers?: DecisionMakers;
  websiteDescription?: string;

  // Step 7
  pricingPreference?: PricingPreference;

  // Step 8
  booking?: {
    uid?: string;
    startTime?: string;
    endTime?: string;
    eventTypeTitle?: string;
  };

  // Step 9
  partnerInviteEmail?: string;

  // Hidden tracking
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  referrer?: string | null;
  landing_page?: string | null;
  entry_plan?: string | null;
};

export const COUNTRY_CODES: { code: string; label: string; dial: string }[] = [
  { code: "us", label: "US", dial: "+1" },
  { code: "ca", label: "CA", dial: "+1" },
  { code: "gb", label: "UK", dial: "+44" },
  { code: "au", label: "AU", dial: "+61" },
  { code: "nz", label: "NZ", dial: "+64" },
  { code: "ie", label: "IE", dial: "+353" },
  { code: "in", label: "IN", dial: "+91" },
  { code: "de", label: "DE", dial: "+49" },
  { code: "fr", label: "FR", dial: "+33" },
  { code: "es", label: "ES", dial: "+34" },
  { code: "it", label: "IT", dial: "+39" },
  { code: "nl", label: "NL", dial: "+31" },
  { code: "se", label: "SE", dial: "+46" },
  { code: "no", label: "NO", dial: "+47" },
  { code: "dk", label: "DK", dial: "+45" },
  { code: "ch", label: "CH", dial: "+41" },
  { code: "at", label: "AT", dial: "+43" },
  { code: "be", label: "BE", dial: "+32" },
  { code: "pt", label: "PT", dial: "+351" },
  { code: "mx", label: "MX", dial: "+52" },
  { code: "br", label: "BR", dial: "+55" },
  { code: "ar", label: "AR", dial: "+54" },
  { code: "za", label: "ZA", dial: "+27" },
  { code: "ae", label: "AE", dial: "+971" },
  { code: "sg", label: "SG", dial: "+65" },
  { code: "jp", label: "JP", dial: "+81" },
];

export const TIMELINE_OPTIONS: { value: LaunchTimeline; label: string }[] = [
  { value: "asap", label: "ASAP / within 2 weeks" },
  { value: "1-2-months", label: "In the next 1 to 2 months" },
  { value: "3-6-months", label: "In the next 3 to 6 months" },
  { value: "exploring", label: "Just exploring for now" },
];

export const DECISION_MAKERS_OPTIONS: { value: DecisionMakers; label: string }[] = [
  { value: "just-me", label: "Just me" },
  { value: "partner-spouse", label: "My business partner or spouse" },
  { value: "team-manager", label: "A team or manager" },
  { value: "other", label: "Other" },
];

export const PRICING_PREFERENCE_OPTIONS: {
  value: PricingPreference;
  title: string;
  subtitle: string;
}[] = [
  {
    value: "launch",
    title: "Pay more upfront, own the site, lower monthly",
    subtitle: "Launch Plan: $1,995 + $149/mo",
  },
  {
    value: "growth",
    title: "Low upfront, everything handled monthly",
    subtitle: "Growth Monthly: $199 setup + $399/mo",
  },
  {
    value: "custom",
    title: "I need something custom or have a bigger budget",
    subtitle: "Custom Build: from $3,495",
  },
  {
    value: "undecided",
    title: "Show me the mockup first, then we'll talk pricing",
    subtitle: "I want to see the quality before deciding",
  },
];

export const WEBSITE_GOALS = [
  "Generate leads & enquiries",
  "Sell products online",
  "Showcase my work / portfolio",
  "Provide information & build trust",
  "Take bookings / appointments",
  "Other",
] as const;

export const INDUSTRIES = [
  "Restaurant / Café",
  "Trades & Services",
  "Real Estate",
  "Health & Fitness",
  "Retail / E-commerce",
  "Beauty & Wellness",
  "Professional Services",
  "Construction",
  "Automotive",
  "Education",
  "Events & Entertainment",
  "Other",
] as const;

export const WEBSITE_STATUS_OPTIONS: { value: WebsiteStatus; label: string }[] = [
  { value: "needs-replacing", label: "Yes - it needs replacing" },
  { value: "terrible", label: "Yes - but it's terrible" },
  { value: "starting-fresh", label: "No - starting fresh" },
];
